import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, ArrowRight, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const planParam = params.get("plan"); // "free" | null
  const promoFromUrl = (params.get("promo") || "").trim();
  const promoMode = !!promoFromUrl; // if ?promo=1 or ?promo=concrete, open UI prefilled

  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "", email: "", phone: "", password: "",
    promo_code: promoFromUrl && promoFromUrl !== "1" ? promoFromUrl : "",
  });
  const [showPromo, setShowPromo] = useState(promoMode);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(form);
      // Promo-applied users land straight on the dashboard (backend grants Pro)
      if (result?.promo_applied || result?.subscription_status === "active") {
        toast.success("Promo code applied — enjoy Pro, free!");
        window.location.href = "/dashboard";
        return;
      }
      if (planParam === "free") {
        toast.success("Account created — welcome to Quick Quotes Free!");
        window.location.href = "/dashboard";
        return;
      }
      toast.success("Account created — redirecting to checkout…");
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  const f = (k, label, type = "text", testid) => {
    const autoComplete = (
      k === "email" ? "email" :
      k === "password" ? "new-password" :
      k === "first_name" ? "given-name" :
      k === "last_name" ? "family-name" :
      k === "company_name" ? "organization" :
      k === "phone" ? "tel" :
      "off"
    );
    return (
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
        <Input type={type} name={k} autoComplete={autoComplete} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required data-testid={testid || `signup-${k}`} />
      </div>
    );
  };

  const headline = promoMode
    ? <>Enter your <b>promo code</b> below — unlock <b>Pro, free</b>.</>
    : planParam === "free"
      ? <>Starting on the <b>Free</b> plan — 3 quotes total. Upgrade anytime.</>
      : <>Next step: secure checkout for <b>$3.99/month</b>. Cancel anytime.</>;

  const buttonLabel = promoMode
    ? <>Unlock Pro with code <ArrowRight className="w-4 h-4 ml-2" /></>
    : planParam === "free"
      ? <>Start Free <ArrowRight className="w-4 h-4 ml-2" /></>
      : <>Continue to payment <ArrowRight className="w-4 h-4 ml-2" /></>;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-lg bg-white border border-slate-200 rounded-xl p-8 shadow-xl shadow-slate-900/5" data-testid="signup-form">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
            <HardHat className="w-5 h-5 text-white" />
          </div>
          <div className="font-display font-extrabold uppercase tracking-widest">Quick Quotes</div>
        </Link>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Create your account</h1>
        <p className="text-sm text-slate-500 mt-1">{headline}</p>

        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {f("first_name", "First name")}
            {f("last_name", "Last name")}
          </div>
          {f("company_name", "Company name")}
          {f("email", "Email", "email")}
          {f("phone", "Phone number", "tel")}
          {f("password", "Password", "password")}

          {showPromo ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4" data-testid="promo-wrap">
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
                <Ticket className="w-3.5 h-3.5" /> Promo code
              </Label>
              <Input
                type="text"
                name="promo_code"
                autoComplete="off"
                value={form.promo_code}
                onChange={(e) => setForm({ ...form, promo_code: e.target.value })}
                placeholder="Enter your code"
                className="mt-1 bg-white"
                data-testid="signup-promo"
              />
              <p className="mt-2 text-[11px] text-blue-700/80">
                A valid code unlocks the Pro plan at no cost. No card required.
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowPromo(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center gap-1"
              data-testid="signup-promo-toggle"
            >
              <Ticket className="w-4 h-4" /> Have a promo code?
            </button>
          )}

          <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="signup-submit">
            {loading ? "Creating…" : buttonLabel}
          </Button>
          <p className="text-xs text-slate-500 text-center">
            {promoMode
              ? "No card charged when a valid promo code is used."
              : <>By continuing you agree to our terms. Secure checkout by Stripe · Cancel anytime.</>}
          </p>
        </div>

        <p className="mt-6 text-sm text-slate-500 text-center">
          Already have an account? <Link to="/login" className="text-blue-600 font-medium hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
